import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { CallSwiperLogo } from "@/components/CallSwiperLogo";
import {
  LayoutDashboard,
  PhoneCall,
  Users,
  BarChart3,
  LogOut,
  Moon,
  Sun,
  Building2,
  Eye,
  ArrowLeftToLine,
  Menu,
  Bell,
  LifeBuoy,
  BellRing,
  AudioLines,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { toast } from "sonner";
import { NotificationsDialog } from "@/components/NotificationsDialog";
import { NotificationBell } from "@/components/NotificationBell";
import { GetHelpDialog } from "@/components/GetHelpDialog";

export function AppLayout() {
  const { user, workspace, logout, impersonation, endImpersonation } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isImpersonating = !!impersonation;

  const clientNav = [
    { to: "/app/calls", label: "Calls", icon: PhoneCall, testId: "nav-calls" },
    { to: "/app/leads", label: "Leads", icon: Users, testId: "nav-leads" },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
    { to: "/app/voices", label: "Voices", icon: AudioLines, testId: "nav-voices" },
    { to: "/app/alerts", label: "Alert Settings", icon: BellRing, testId: "nav-alerts" },
  ];
  const adminNav = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "nav-admin-overview", end: true },
    { to: "/admin/workspaces", label: "Workspaces", icon: Building2, testId: "nav-admin-workspaces" },
    { to: "/admin/users", label: "Users", icon: Users, testId: "nav-admin-users" },
    { to: "/admin/voices", label: "Voices", icon: AudioLines, testId: "nav-admin-voices" },
  ];
  const nav = isAdmin ? adminNav : clientNav;

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login");
    setMobileOpen(false);
  };

  const handleReturnToAdmin = async () => {
    await endImpersonation();
    toast.success("Returned to admin console");
    navigate("/admin/workspaces");
    setMobileOpen(false);
  };

  // Close mobile drawer when route changes
  const currentActiveLabel = nav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "";

  const SidebarBody = ({ onNavigate }) => (
    <>
      <div className={`h-16 flex items-center px-5 border-b ${isImpersonating ? "border-amber-500/30" : "border-border"}`}>
        <CallSwiperLogo size={24} />
      </div>

      <div className="px-3 pt-4 pb-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground px-2 mb-2">
          {isImpersonating ? "Impersonating" : isAdmin ? "Admin Console" : "Workspace"}
        </p>
        {!isAdmin && workspace && (
          <div className="px-2 py-1.5 mb-2 flex items-center gap-2 text-sm">
            <Building2 className={`w-3.5 h-3.5 ${isImpersonating ? "text-amber-500" : "text-muted-foreground"}`} />
            <span className="font-semibold truncate">{workspace.name}</span>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm relative transition-colors min-h-[44px] ${
                isActive
                  ? "bg-primary/10 text-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-primary rounded-r" />}
                <item.icon className="w-4 h-4" />
                <span className="font-medium">{item.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-muted/60 transition-colors min-h-[44px]"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-semibold truncate">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggle} data-testid="theme-toggle-menu">
              {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setNotifOpen(true)} data-testid="notifications-menu-item">
              <Bell className="w-4 h-4 mr-2" /> Notifications
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHelpOpen(true)} data-testid="get-help-menu-item">
              <LifeBuoy className="w-4 h-4 mr-2" /> Get help
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="min-h-screen w-full flex bg-background text-foreground">
      {/* Desktop sidebar (>=md) */}
      <aside
        className={`hidden md:flex w-64 shrink-0 border-r flex-col bg-card safe-top safe-bottom safe-left ${isImpersonating ? "border-amber-500/40" : "border-border"}`}
        data-testid="sidebar"
      >
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className={`w-72 p-0 flex flex-col bg-card safe-top safe-bottom safe-left ${isImpersonating ? "border-amber-500/40" : ""}`}
          data-testid="mobile-sidebar"
        >
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {isImpersonating && (
          <div
            className="w-full bg-amber-500/95 dark:bg-amber-500/90 text-amber-950 border-b border-amber-600 px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 sticky top-0 z-30 safe-top"
            data-testid="impersonation-banner"
          >
            <div className="flex items-start sm:items-center gap-3 min-w-0">
              <span className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-950/15 border border-amber-950/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]">
                <Eye className="w-3 h-3" /> Impersonation Mode
              </span>
              <p className="text-xs sm:text-sm font-medium sm:truncate">
                Viewing as <span className="font-bold">{impersonation?.workspace_name}</span>
                <span className="hidden sm:inline"> — actions will affect the client's workspace.</span>
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReturnToAdmin}
              className="shrink-0 bg-amber-950 text-amber-50 border-amber-950 hover:bg-amber-900 hover:text-amber-50 w-full sm:w-auto min-h-[40px]"
              data-testid="return-to-admin-btn"
            >
              <ArrowLeftToLine className="w-3.5 h-3.5 mr-1.5" />
              <span className="sm:inline">Return to Admin Console</span>
            </Button>
          </div>
        )}
        <header className={`h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border bg-background/70 backdrop-blur sticky top-0 z-10 ${isImpersonating ? "" : "safe-top"}`}>
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden -ml-2 h-12 w-12"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="!w-6 !h-6" />
            </Button>
            {/* Mobile-only inline logo (desktop shows workspace text) */}
            <div className="md:hidden flex items-center min-w-0">
              <CallSwiperLogo size={22} showText={false} />
              <span className="ml-2 text-lg font-bold truncate">
                {isImpersonating
                  ? impersonation?.workspace_name
                  : isAdmin
                  ? currentActiveLabel || "Admin"
                  : workspace?.name || "Workspace"}
              </span>
            </div>
            <div className="hidden md:flex text-sm text-muted-foreground items-center gap-2">
              {isImpersonating ? (
                <>
                  <Eye className="w-3.5 h-3.5 text-amber-500" />
                  <span>
                    Viewing as <span className="text-foreground font-semibold">{impersonation?.workspace_name}</span>
                  </span>
                </>
              ) : isAdmin ? (
                "Call Swiper Control Panel"
              ) : (
                workspace?.name || "Workspace"
              )}
            </div>
          </div>
          <NotificationBell />
        </header>
        <div className="flex-1 px-5 py-4 sm:p-6 md:p-8 max-w-[1440px] w-full mx-auto animate-fade-in-up">
          <Outlet />
        </div>
      </main>

      <NotificationsDialog open={notifOpen} onOpenChange={setNotifOpen} />
      <GetHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}
